<template>
    <div class="bg-[#080808]">
        <h2 class="lg:text-title-xl text-title-md font-bold tracking-tighter text-gray-100 mb-6 text-center">
            {{ data.title }}
        </h2>

        <div class="mx-auto" @touchstart="pauseAutoplay" @touchend="resumeAutoplay">
            <AppCarouselSlider
                :slides-per-view="1"
                effect="fade"
                :autoplay="{
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                }"
                @reach-end="
                    () => {
                        activeSlideIndex = 0;
                    }
                "
                :navigation="false"
                :modules="[SwiperNavigation, SwiperPagination, SwiperAutoplay, SwiperEffectFade]"
                @autoplayTimeLeft="onAutoplayTimeLeft"
                @slide-change="sliderChangeWatch"
                class="mySwiper"
            >
                <template #item="{ slide }">
                    <AppCompareSlider
                        img-class="w-full h-[518px] md:h-[800px] lg:max-h-[800px]  object-cover "
                        :item="slide"
                    >
                        <template #before-badge>
                            <div
                                class="px-2 py-1 rounded-lg bg-[#00000033] top-6 left-6 text-gray-200 leading-6 text-body-sm absolute"
                            >
                                {{ $t("global_before") }}
                            </div>
                        </template>
                        <template #after-badge>
                            <div
                                class="px-2 py-1 rounded-lg bg-[#00000033] top-6 right-6 text-gray-200 leading-6 text-body-sm absolute"
                            >
                                {{ $t("global_after") }}
                            </div>
                        </template>
                    </AppCompareSlider>
                </template>
                <template #container-start>
                    <div
                        class="p-4 w-[245px] absolute z-50 bottom-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#00000066] backdrop-blur-[8px] rounded-3xl"
                    >
                        <div class="flex justify-between items-center">
                            <div
                                @click="previousSlide()"
                                class="w-8 h-8 bg-[#00000033] rounded-full flex justify-center items-center cursor-pointer"
                            >
                                <Icon name="tabler:chevron-left" class="w-4 h-4 text-gray-100" />
                            </div>
                            <div class="flex space-x-2">
                                <div v-for="(slide, index) in items?.length" class="flex space-x-2">
                                    <div
                                        v-if="index !== activeSlideIndex"
                                        @click="goToSlide(index)"
                                        class="w-2 h-2 bg-[#FFFFFF33] rounded-full cursor-pointer"
                                    ></div>
                                    <div v-else class="autoplay-progress">
                                        <div class="bg-[#FFFFFF33] w-14 h-2 rounded-full relative">
                                            <div
                                                ref="progressCircle"
                                                :style="`width: ${properWidth}`"
                                                class="w-0 h-2 rounded-full bg-gray-400"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                @click="nextSlide()"
                                class="w-8 h-8 bg-[#00000033] rounded-full flex justify-center items-center cursor-pointer"
                            >
                                <Icon name="tabler:chevron-right" class="w-4 h-4 text-gray-100" />
                            </div>
                        </div>
                    </div>
                </template>
            </AppCarouselSlider>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { IImageSlider } from "~/types/components/landings.type";

const { initSwiper, activeSlideIndex, slider, items, goToSlide } = useCarousel();

interface Props {
    data: IImageSlider;
}
const props = defineProps<Props>();

const sliderChangeWatch = () => {
    activeSlideIndex.value = slider.value.activeIndex;
};

const pauseAutoplay = () => {
    slider.value.autoplay.pause();
};
const resumeAutoplay = () => {
    slider.value.autoplay.resume();
};
const progressCircle = ref(null);
const properWidth = ref();
const onAutoplayTimeLeft = (s, time, progress) => {
    properWidth.value = progress * 100 + "%";
};
const previousSlide = () => {
    if (activeSlideIndex.value === 0) {
        goToSlide(items.value.length);
    } else {
        activeSlideIndex.value--;
        slider.value.slidePrev();
    }
};
const route = useRoute();

watch(
    route,
    (value) => {
        activeSlideIndex.value = 0;
    },
    { deep: true }
);

const nextSlide = () => {
    if (activeSlideIndex.value + 1 === items.value.length) {
        goToSlide(0);
    } else {
        activeSlideIndex.value++;
        slider?.value?.slideNext();
    }
};

onMounted(() => {
    initSwiper(props.data.slides);
});
</script>

<style scoped>
.autoplay-progress {
    position: relative;
}

.autoplay-progress > div > div {
    position: absolute;
    right: 0;
    top: 0px;
    z-index: 10;
    height: 100%;
}
</style>
